import { isPlatformBrowser } from '@angular/common';
import { Inject, inject, Injectable, OnDestroy, PLATFORM_ID, signal } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Data, Router } from '@angular/router';
import { AuthService } from '@core/auth.service';
import { AuthGamesService } from '@core/AuthServices/authGames/auth-games.service';
import { AuthPartialRegService } from '@core/AuthServices/authPartialReg/auth-partial-reg.service';
import {
    buildLocalPushBannerContent,
    resolvePushToastLanguage,
} from '@core/push-notification-toast.i18n';
import { BehaviorSubject, firstValueFrom, map, Observable, of, Subject, Subscription, switchMap, take, takeUntil } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { CommonVariable } from 'src/app/app.constants';
import { resolveInlineCandidateList, selectDeviceInlineNotification } from 'src/app/components/add-home-screen-banner/add-home-screen.helpers';
import { InboxMessageComponent } from 'src/app/components/inbox-message/inbox-message.component';
import { CommonService } from 'src/app/services/common/common.service';
import { HomeDataService } from 'src/app/services/home-data.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { OrientationService } from 'src/app/services/orientation.service';

export interface NotificationState {
    details: Data;
}

export interface CountdownState {
    seconds: number | null;
}

@Injectable({
    providedIn: null
})
export class SyndicateNotificationService implements OnDestroy {

    isAutoShowView: boolean;
    inlineNotificationDetails: Data;
    private router = inject(Router);
    private countdownSub: Subscription;
    private destroy$ = new Subject<void>();
    public countdownSeconds: number | null;
    private authService = inject(AuthService)
    private bottomSheet = inject(MatBottomSheet);
    private commonservice = inject(CommonService);
    @Inject(PLATFORM_ID) private platformId: object
    isVoucherClaimedSignal = signal<boolean>(false);
    private inboxAutoShowSubscription: Subscription;
    private homeDataService = inject(HomeDataService)
    private AuthGamesService = inject(AuthGamesService);
    private orientationService = inject(OrientationService)
    languageCode: string = CommonVariable.preferredLanguage;
    private notificationsService = inject(NotificationsService)
    private AuthPartialRegService = inject(AuthPartialRegService)
    private shouldShowInboxSubject = new BehaviorSubject<boolean>(false);
    private countdownSubject = new BehaviorSubject<CountdownState | null>(null);
    private notificationSubject = new BehaviorSubject<NotificationState | null>(null);
    private expiryTimer: ReturnType<typeof setTimeout> | undefined;
    private lastPageDetails: Data | undefined;
    readonly shouldShowInbox$: Observable<boolean> = this.shouldShowInboxSubject.asObservable();
    readonly countdown$: Observable<CountdownState | null> = this.countdownSubject.asObservable();
    readonly notification$: Observable<NotificationState | null> = this.notificationSubject.asObservable();
    constructor() { }

    /**
     * Initialize the notification service
     * Called once from page ngOnInit
     */
    initialise(userId: string, syndicateId: string): void {
        // Placeholder - actual implementation would set up notification listeners
        // This is a stub that maintains compatibility with the refactoring
    }

    /**
     * Set notification data from external source (e.g., page component)
     */
    setNotification(notification: NotificationState): void {
        this.notificationSubject.next(notification);
    }

    /**
     * Set countdown seconds from external source
     */
    setCountdown(seconds: number | null): void {
        this.countdownSubject.next({ seconds });
    }

    /**
     * Signal that inbox should be shown
     */
    triggerInboxShow(): void {
        this.shouldShowInboxSubject.next(true);
    }

    /**
     * Reset inbox show trigger
     */
    resetInboxShow(): void {
        this.shouldShowInboxSubject.next(false);
    }

    ngOnDestroy(): void {
        if (this.expiryTimer) {
            clearTimeout(this.expiryTimer);
            this.expiryTimer = undefined;
        }
        this.countdownSub?.unsubscribe();
        this.countdownSub = undefined;
        this.destroy$.next();
        this.destroy$.complete();
        this.notificationSubject.complete();
        this.countdownSubject.complete();
        this.shouldShowInboxSubject.complete();
    }
    validateNotifications(syndicateDetails: Data): void {
        try {
            if (this.expiryTimer) {
                clearTimeout(this.expiryTimer);
                this.expiryTimer = undefined;
            }
            this.lastPageDetails = syndicateDetails;

            const candidateList = resolveInlineCandidateList(syndicateDetails);
            const notification = this.selectActiveInlineNotification(candidateList);
            if (!notification) {
                this.dismissExpiredToRebuild(candidateList);
                this.clearNotification();
                return;
            }
            this.applyNotificationDetails(notification);
            this.scheduleExpiryRevalidation(notification);
        } catch (error) {
            this.authService.errorHandlerLogs('Error validating syndicate inline notifications:', error);
            this.clearNotification();
        }
    }

    private dismissExpiredToRebuild(inlineNotifications: Data[] | undefined): void {
        if (!Array.isArray(inlineNotifications)) {
            return;
        }

        const now = Date.now();
        for (const notification of inlineNotifications) {
            if (notification?.dismissed) {
                continue;
            }
            const expiryMs = notification?.expiry_date?.seconds
                ? notification.expiry_date.seconds * 1000
                : 0;
            if (!expiryMs || expiryMs <= now) {
                const path = notification?.notification_id?.path;
                if (path) {
                    notification.dismissed = true;
                    this.notificationsService.updateClaiminlineVoucherDismiss(path);
                }
                return;
            }
        }
    }

    private scheduleExpiryRevalidation(notification: Data): void {
        const expiryMs = notification?.expiry_date?.seconds
            ? notification.expiry_date.seconds * 1000
            : 0;
        if (!expiryMs) {
            return;
        }

        const delay = expiryMs - Date.now();
        const run = () => {
            this.expiryTimer = undefined;
            const next = this.selectActiveInlineNotification(resolveInlineCandidateList(this.lastPageDetails));
            if (next) {
                this.applyNotificationDetails(next);
                this.scheduleExpiryRevalidation(next);
                return;
            }
            const path = notification?.notification_id?.path;
            if (path) {
                notification.dismissed = true;
                this.notificationsService.updateClaiminlineVoucherDismiss(path);
            }
            this.clearNotification();
        };

        if (delay <= 0) {
            run();
            return;
        }

        this.expiryTimer = setTimeout(run, delay + 100);
    }

    /** Device-local selection from account-neutral candidates (AC1/AC4–AC6/AC9). */
    private selectActiveInlineNotification(inlineNotifications: Data[] | undefined): Data | null {
        return selectDeviceInlineNotification(inlineNotifications as Array<Record<string, unknown>>);
    }

    private clearNotification(): void {
        this.inlineNotificationDetails = {};
        this.countdownSeconds = undefined;
        this.countdownSub?.unsubscribe();
        this.countdownSub = undefined;
        this.notificationSubject.next({ details: {} });
        this.countdownSubject.next({ seconds: null });
    }

    private applyNotificationDetails(notification: Data): void {
        const iconUrl = this.getIconUrl(notification?.icon_url);
        if (iconUrl) this.preloadImage(iconUrl);
        this.inlineNotificationDetails = { ...(notification || {}) };
        this.notificationSubject.next({
            details: this.inlineNotificationDetails,
        });

        const inlineId = this.inlineNotificationDetails?.notification_id?.id;
        const seconds = this.inlineNotificationDetails?.expiry_date?.seconds;
        this.countdownSub?.unsubscribe();
        this.countdownSub = undefined;
        if (this.notificationsService && seconds != null && inlineId) {
            this.countdownSub = this.notificationsService
                .startCountdownMonitor(seconds, inlineId)
                .pipe(takeUntil(this.destroy$))
                .subscribe((res) => {
                    this.countdownSeconds = res;
                    this.countdownSubject.next({ seconds: res });
                });
        } else {
            this.countdownSeconds = undefined;
            this.countdownSubject.next({ seconds: null });
        }
    }

    private isPushNotificationDetails(details: Data | null | undefined): boolean {
        const id = details?.notification_id?.id || details?.id;
        if (id === "push_notification") {
            return true;
        }
        const actionDetails = details?.action?.details?.toLowerCase?.() || "";
        return (
            actionDetails === "enable_push_notifications" ||
            actionDetails === "enable push notifications"
        );
    }

    /**
     * After settings language change, re-pick content[lang] from the source
     * inline_notification doc so the Syndicate slot is not stuck on flattened
     * English from main_pages. Push banners fall back to local i18n when
     * content[lang] is missing.
     */
    async resolveInlineContentForLanguage(language: string): Promise<void> {
        const details = this.inlineNotificationDetails;
        if (!details?.content || typeof details.content !== "object") {
            return;
        }
        const code = resolvePushToastLanguage(language, null, null);
        const publish = (content: Data): void => {
            this.inlineNotificationDetails = {
                ...details,
                content: { ...content },
            };
            this.notificationSubject.next({
                details: this.inlineNotificationDetails,
            });
        };
        const applyLocalPushContent = (): void => {
            if (!this.isPushNotificationDetails(details) || code === "en") {
                return;
            }
            publish(buildLocalPushBannerContent(code));
        };
        const path = details?.notification_id?.path;
        if (!path) {
            applyLocalPushContent();
            return;
        }
        try {
            const snap = await firstValueFrom(
                this.authService.getAngularFireStore(path, "get").pipe(
                    take(1),
                    timeout({ each: 3000 }),
                    catchError(() => of(null))
                )
            );
            const data =
                snap && typeof (snap as Data).data === "function"
                    ? (snap as Data).data()
                    : null;
            const contentMap = data?.content;
            if (!contentMap || typeof contentMap !== "object") {
                applyLocalPushContent();
                return;
            }
            const langEntry = contentMap[code];
            const enEntry = contentMap.en;
            const isLangBlock = (entry: unknown): entry is Data =>
                !!entry &&
                typeof entry === "object" &&
                !Array.isArray(entry) &&
                ((entry as Data).title != null ||
                    (entry as Data).line_1 != null ||
                    (entry as Data).button_label != null);
            if (!isLangBlock(langEntry) && !isLangBlock(enEntry)) {
                applyLocalPushContent();
                return;
            }
            if (!isLangBlock(langEntry) && code !== "en") {
                applyLocalPushContent();
                return;
            }
            const resolved = isLangBlock(langEntry)
                ? langEntry
                : isLangBlock(enEntry)
                    ? enEntry
                    : details.content;
            // Push only: local fills gaps (e.g. missing line_1_alt). Never merge
            // push fallback copy into non-push inline notifications.
            if (this.isPushNotificationDetails(details)) {
                publish({ ...buildLocalPushBannerContent(code), ...resolved });
            } else {
                publish({ ...resolved });
            }
        } catch (error) {
            applyLocalPushContent();
            this.authService.errorHandlerLogs(
                "Error resolving Syndicate inline notification language",
                error
            );
        }
    }
    getIconUrl(iconUrl: string | undefined): string {
        return this.authService.getIconUrl(iconUrl);
    }
    preloadImage(url?: string) {
        // Only run in browser (safe for PWA and app)
        if (!url || !isPlatformBrowser(this.platformId)) return;

        const img = new Image();
        img.decoding = 'async';
        img.loading = 'eager';
        img.src = url;
    }
    handleInlineNotification(notificationDetails: Data) {
        if (notificationDetails?.action?.type === "nav" && notificationDetails?.action?.details) {
            const actionDetails = notificationDetails.action.details;
            if (actionDetails?.includes("http")) {
                this.openTypeFormForFeedback(notificationDetails);
            } else {
                this.router.navigateByUrl(actionDetails)?.catch((error) => {
                    return error;
                });
            }
            this.feedBackDismiss();
        }

        if (notificationDetails?.action?.type === "script") {
            this.isVoucherClaimedSignal.set(true);
            this.notificationsService.onClaiminlinevoucher(notificationDetails).then((result: Data) => {
                if (result) this.isVoucherClaimedSignal.set(false);
            }).catch((error) => {
                this.authService.errorHandlerLogs("Error claiming voucher:", error);
            });
        }
    }

    feedBackDismiss(): void {
        const route = this.inlineNotificationDetails?.notification_id?.path;
        this.clearNotification();
        if (!route) {
            return;
        }
        const doc_id = route?.substring(route.lastIndexOf("/") + 1) || null;
        const path_id = "pGxBYtRO4mdgNkboYdSy";
        const doc_data = { dismissed: true, viewed: true };
        const dv = { docId: doc_id };
        const method = "update";
        this.authService.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data,
            dv,
            method,
        }).subscribe();
    }

    openTypeFormForFeedback(notification: Data) {
        const language = this.languageCode;

        if (
            notification?.action?.type === "nav" &&
            !notification?.viewed &&
            notification?.action?.details?.includes("typeform.com")
        ) {
            return this.commonservice.getUid("AuthGamesService").pipe(
                switchMap((userId) => {
                    if (!userId) return of(null);

                    return this.authService
                        .getAngularFireStore(`/users/${userId}/balance/current`, "get")
                        .pipe(
                            map((res: Data) => ({
                                userId,
                                balance: res?.data(),
                            }))
                        );
                })
            )
                .subscribe({
                    next: ({ userId, balance }) => {
                        if (!balance) return;

                        const credit_balance = balance?.preferred_currency?.credit || null;
                        const Point_balance = balance?.points || null;

                        const hiddenData = {
                            user_id: userId,
                            Point_balance,
                            Credit_Balance: credit_balance,
                            Language: language || "en",
                            User_State: null,
                        };

                        const url = notification?.action?.details;
                        this.AuthGamesService.typeForm(url, hiddenData, notification);
                    },
                });
        }

        return;
    }
    showInboxMessage() {
        setTimeout(() => {
            if (!this.isAutoShowView) {
                this.isAutoShowView = true;
                // Clean up any previous subscription before creating a new one
                this.inboxAutoShowSubscription?.unsubscribe();

                this.inboxAutoShowSubscription = this.homeDataService?.userInbox$
                    ?.pipe(
                        take(1),
                        map((data: any) => {
                            if (!data || !data?.length) return [];
                            return data.filter((values: any) =>
                                values?.auto_show &&
                                !values.viewed &&
                                this.router.url.includes("syndicate") &&
                                (values?.auto_show_inbox_page === "syndicate" || values?.auto_show_inbox_page === "all")
                            );
                        })
                    )
                    ?.subscribe((data: Data[]) => {
                        if (!data || !data?.length) return;
                        if (data[0]?.inbox_template) {
                            this.AuthPartialRegService?.openTemplate(data[0]);
                        } else {
                            this.openMessage(data[0]);
                        }
                    });
            }
        }, 3000);
    }
    openMessage(message: Data) {
        if (!message) return;
        if (this.authService.templateNameArray.includes(message?.template_name)) {
            this.AuthPartialRegService.openAutoShowInbox(message);
        } else {
            const panelClass = this.orientationService.isFullscreen()
                ? "inbox-bottom-sheet-fullscreen"
                : message?.template_type === "plunko_christmas" || message?.template_type === "valentine_plunko_campaign"
                    ? "bottom-sheet-fullscreen"
                    : "inbox-bottom-sheet";
            this.bottomSheet.open(InboxMessageComponent, {
                panelClass: panelClass,
                data: message,
            });
        }
    }

    teardown(): void {
        if (this.expiryTimer) {
            clearTimeout(this.expiryTimer);
            this.expiryTimer = undefined;
        }
        this.countdownSub?.unsubscribe();
        this.countdownSub = undefined;
        this.inboxAutoShowSubscription?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
        this.destroy$ = new Subject<void>();
    }
}
